import {
  AlertCircle,
  AlertTriangle,
  Mail,
  Phone,
  RefreshCw,
  ShieldCheck,
  Wrench,
} from 'lucide-react'
import { type ReactNode, useEffect, useRef } from 'react'

import { AGENCY_EMAIL, AGENCY_PHONE } from '@/lib/constants/agency'

import type { ReservationErrorType } from './reservation-error'

interface ReservationErrorCardProps {
  /** Message renvoyé par l'API, déjà rédigé pour le client. */
  message: string
  type: ReservationErrorType
}

const TITLES = {
  error: "Votre réservation n'a pas pu être enregistrée",
  maintenance: 'Réservation momentanément indisponible',
  uncertain: 'Réservation à vérifier avec nous',
} satisfies Record<ReservationErrorType, string>

const ICONS = {
  error: AlertCircle,
  maintenance: Wrench,
  uncertain: AlertTriangle,
} satisfies Record<ReservationErrorType, typeof AlertCircle>

/**
 * Erreur affichée en ligne à l'étape « Informations », dans le même registre que
 * CheckoutErrorCard au paiement. Volontairement inline plutôt qu'en boundary
 * d'erreur : le formulaire passagers vient d'être rempli, on ne veut pas le perdre
 * pour une panne côté fournisseur.
 */
export function ReservationErrorCard({ message, type }: ReservationErrorCardProps) {
  const Icon = ICONS[type]
  const cardRef = useRef<HTMLDivElement>(null)

  // Le bouton « Je réserve » est en bas d'un formulaire long : sans ce scroll,
  // l'erreur peut apparaître hors écran et passer inaperçue.
  useEffect(() => {
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [type, message])

  return (
    <div
      className="flex w-full flex-col gap-3 rounded-2xl border border-danger/30 bg-danger-subtle p-5"
      ref={cardRef}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white"
        >
          <Icon className="h-4 w-4 text-danger" />
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-base leading-none font-semibold text-danger-foreground">
            {TITLES[type]}
          </div>
          <p className="text-sm leading-relaxed text-danger-foreground">{message}</p>
        </div>
      </div>

      <ul className="ml-11 space-y-2 text-sm text-danger-foreground">{getErrorListItems(type)}</ul>
    </div>
  )
}

function getErrorListItems(type: ReservationErrorType): ReactNode {
  if (type === 'uncertain') {
    return (
      <>
        <li className="flex items-start gap-2">
          <Phone className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Appelez-nous au{' '}
            <a className="font-medium underline" href={`tel:${AGENCY_PHONE.replaceAll(/\s/g, '')}`}>
              {AGENCY_PHONE}
            </a>
          </span>
        </li>
        <li className="flex items-start gap-2">
          <Mail className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Ou écrivez-nous à{' '}
            <a className="font-medium underline" href={`mailto:${AGENCY_EMAIL}`}>
              {AGENCY_EMAIL}
            </a>
          </span>
        </li>
        <li className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Ne relancez pas la réservation : un dossier a peut-être déjà été ouvert chez la
            compagnie
          </span>
        </li>
      </>
    )
  }

  if (type === 'maintenance') {
    return (
      <>
        <li className="flex items-start gap-2">
          <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Relancez la réservation dans quelques minutes : vos informations sont conservées
          </span>
        </li>
        <li className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Aucun montant n&apos;a été débité, aucune autre action n&apos;est requise</span>
        </li>
      </>
    )
  }

  return (
    <>
      <li className="flex items-start gap-2">
        <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Réessayez dans quelques instants</span>
      </li>
      <li className="flex items-start gap-2">
        <Phone className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Si le problème persiste, contactez-nous au{' '}
          <a className="font-medium underline" href={`tel:${AGENCY_PHONE.replaceAll(/\s/g, '')}`}>
            {AGENCY_PHONE}
          </a>
        </span>
      </li>
    </>
  )
}
